<script>
import {
    Query
} from "@syncfusion/ej2-data/src/query";
import { TabItemsDirective, TabItemDirective } from "@syncfusion/ej2-vue-navigations/src/tab/items.directive";
import { library } from "@common/library/library";
import { componentStore } from "@common/componentStore/componentStore";
import { configStore } from "@common/configStore";
import { defineAsyncComponent } from "@common/defineAsyncComponent";

const sdsConsolePanelCfg = {
    cId: "sdsConsolePanel",
    cType: "sdsConsolePanel",
    cTitle: "Console Panel",
    showInCmpList: false,
    cTemplate: `<div class="sds-console-panel-container container-fluid">
                    <div class="row" style="height: 100%;">
                        <div class="col-4" style="padding-right: 0; padding-left: 0;">
                            <div class="sds-console-panel-function-list">
                                <div class="sds-console-panel-function-list-inner">
                                    <div class="sds-console-panel-function-list-inner2">
                                        <p>
                                            <strong>sDs Console</strong>
                                            <button type="button" class="e-link e-btn" style="float: right;" @click="showInstructions = !showInstructions">Instructions</button>
                                        </p>
                                        <ol v-if="showInstructions">
                                            <li>Choose an sDs function</li>
                                            <li>Fill in the desired parameters (or provide a network tab payload)</li>
                                            <li>Press "Send Request"</li>
                                        </ol>
                                        <p>Execute on the <strong>{{currentInstance}}</strong> instance</p>
                                        <control name="FUNCTION_LIST" :items="functionList"></control>
                                        <div>
                                            <ejs-tab id="sds-function-tabs" style="margin-top: 20px;" :selected="selectTab" :selected-item="selectedTabIndex">
                                                <div class="e-tab-header">
                                                    <div>Parameters</div>
                                                    <div>Payload</div>
                                                    <div>Definition</div>
                                                    <div>Statistics</div>
                                                </div>
                                                <div class="e-content">
                                                    <div>
                                                        <div v-if="!fParams.length" class="alert alert-info">
                                                            There are no Parameters for this API function.
                                                        </div>
                                                        <div v-if="loadingParams">
                                                            <control name="FUNCTION_PARAM_LIST" :items="fParams"></control>
                                                        </div>
                                                        <div class="send-buttons-container">
                                                            <control name="GET_BUTTON" class="sds-console-panel-button"></control>
                                                            <control name="SEND_BUTTON" class="sds-console-panel-button"></control>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <control name="REQUEST_AS_TEXT"></control>
                                                        <div class="send-buttons-container">
                                                            <control name="SEND_AS_TEXT_BUTTON" class="sds-console-panel-button"></control>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div class="sds-console-panel-desc">
                                                            <span v-if="selectedFunction.def" ><strong>Definition:</strong> {{selectedFunction.def}}</span><br/>
                                                            <span v-if="selectedFunction.desc" v-text="selectedFunction.desc"></span>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div v-if="statisticInfo" class="sds-console-panel-statisticInfo">{{statisticInfo}}</div>
                                                        <div v-else>Send a request to get statistic info.</div>
                                                    </div>
                                                </div>
                                            </ejs-tab>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-8">
                            <div class="results-column">
                                <div style="display: flex;">
                                    <div style="flex-grow: 1;"><i>Results:</i></div>
                                    <control name="COPY_CLIP_BUTTON" class="sds-console-panel-button" :disabled="!sendResultsHtml && !sendResultsText"></control>
                                </div>
                                <div class="sds-console-panel-results">
                                    <div id="sendResultsHtml" v-html="sendResultsHtml" v-text="sendResultsText"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>`,
    controls: [
        {
            name: 'FUNCTION_PARAM_LIST',
            type: 'list',
            itemContentTemplate: `<div class="sds-console-panel-fparam"><control name="FUNCTION_PARAM" :config="{floatLabelType: 'Always'}" :placeholder="data.name"></control></div>`
        },
        {
            name: 'FUNCTION_PARAM',
            type: "string",
            onchange: "storeParam"
        },
        {
            name: 'REQUEST_AS_TEXT',
            type: "text",
            placeholder: "Paste form data from the browser's network tab here, or click 'Convert Params to Payload' in the params tab.",
            ref: "requestAsTextArea",
            rows: 10,
            oninput: 'payloadInput'
        },
        {
            name: 'FUNCTION_LIST',
            type: "dropdown",
            placeholder: "API function",
            onchange: "setFunction",
            popupHeight: "420px",
            floatLabelType: 'Always',
            helpText: 'Choose the sDs Request you want to test.',
            filterBarPlaceholder: "Search for an API function",
            allowFiltering: true,
            onfiltering: "filtering",
            fields: { text: 'name', value: 'name' },
        },
        {
            name: 'SEND_BUTTON',
            type: 'button',
            label: 'Send Request',
            onclick: 'sendRequest',
            cssClass: 'e-block e-info send-button'
        },
        {
            name: 'SEND_AS_TEXT_BUTTON',
            type: 'button',
            label: 'Send Request',
            onclick: 'sendRequestAsTxt',
            cssClass: 'e-block e-info send-button'
        },
        {
            name: 'GET_BUTTON',
            type: 'button',
            label: 'Convert Params to Payload',
            cssClass: 'e-link',
            onclick: 'getRequestText',
        },
        {
            name: 'COPY_CLIP_BUTTON',
            type: 'button',
            label: 'Copy to clipboard',
            onclick: 'copyToClipboard',
            disabled: true
        },
        {
            name: 'PASTE_CLIP_BUTTON',
            type: 'button',
            label: 'Paste from clipboard',
            onclick: 'pasteFromClipboard',
            disabled: false
        },
    ]
};

const sdsConsolePanelCmp = {
    name: 'sdsConsolePanel',
    props: {
        selectedInstance: {
            default: false
        }
    },
    data() {
        return {
            functionList: null,
            selectedTabIndex: 0,
            showInstructions: false,
            selectedFunction: {},
            fParams: {},
            sendResultsClipboard: '',
            sendResultsText: '',
            sendResultsHtml: '',
            statisticInfo: '',
            localInstance: false,
            loadingParams: false,
            availablePostParams: ['postData[functionName]', 'postData[functionParams]', 'PROJECT_NAME', 'VUE_PROJECT_NAME']
        };
    },
    computed: {
        currentInstance() {
            return this.selectedInstance || this.localInstance;
        }
    },
    methods: {
        filtering: function (e) {
            let searchData = this.functionList;
            let query = new Query();
            query = (e.text !== "") ? query.where("text", "contains", e.text, true) : query;
            e.updateData(searchData, query);
        },
        async onLoad() {
            library.showLoadingIndicator();
            if (!this.currentInstance && typeof IS_JEST === 'undefined') {
                let instance = await configStore.getDevDashLocalInstanceDetails();
                this.localInstance = instance.name;
            }
            this.sDs.post("sds_getFunctionList").then((response) => {
                if (response.status === 'success') {
                    let obj = response.data;
                    Object.keys(obj).forEach(function (key) {
                        obj[key].text = obj[key].name;
                        obj[key].value = obj[key].name;
                    });
                    this.functionList = obj;
                    library.hideLoadingIndicator();
                    this.controls['FUNCTION_LIST'].value = 'sds_getFunctionList';
                }
            });
        },
        storeParam() {
            Object.keys(this.fParams).forEach((key) => {
                this.fParams[key].value = this.controls['FUNCTION_PARAM__' + key].value;
            });
        },
        copyToClipboard() {
            library.copyTextToClipboard(this.sendResultsClipboard);
        },
        pasteFromClipboard() {
            setTimeout(async () => {
                this.refs['requestAsTextArea'].focusIn();
                let text = await navigator.clipboard.readText();
                this.controls['REQUEST_AS_TEXT'].value = text;
            }, 100); // NOSONAR
        },
        async sendRequestAsTxt() {
            let theText = this.sendResultsText;
            if (!theText) theText = this.controls['REQUEST_AS_TEXT'].value;
            theText = theText.trim();
            theText = this.fixPayload(theText);
            this.sendResultsText = '';
            this.sendResultsHtml = '';
            this.sendResultsClipboard = '';
            this.statisticInfo = '';
            this.storeParam();

            if (!theText.trim()) {
                library.showToast('Cannot find request, it should start like postData[functionName]: ...');
                return;
            }
            let t = theText.split('postData[functionName]:');
            let tReqs = [];
            t.forEach((reqText) => {
                if (reqText) tReqs[tReqs.length] = 'postData[functionName]:' + reqText;
            });
            let parseRegexMatches = (tmpRegex, tmpMatch, tmpfParam, text) => {
                let m = tmpMatch;
                while (m !== null) {
                    if (m.index === tmpRegex.lastIndex) tmpRegex.lastIndex++;
                    if (m[2]) { // NOSONAR
                        if (!tmpfParam[m[1]]) tmpfParam[m[1]] = [m[3].trim()]; // NOSONAR
                        else tmpfParam[m[1]][tmpfParam[m[1]].length] = m[3].trim(); // NOSONAR
                    } else tmpfParam[m[1]] = m[3].trim(); // NOSONAR
                    tmpfParam[m[1]] = library.isJson(tmpfParam[m[1]]) ? JSON.parse(tmpfParam[m[1]]) : tmpfParam[m[1]];
                    m = tmpRegex.exec(text);
                }
            };
            for (let i = 0; i < tReqs.length; i++) {
                let text = tReqs[i];
                let regex1 = /"?postData\[functionName\]"?:(.*)/gmi;
                let m1 = regex1.exec(text);
                if (!m1) {
                    library.showToast('Cannot find request, it should start like postData[functionName]: ...');
                    return;
                }
                let fName = m1[1].replace('"', '').trim();
                if (!fName) continue;

                let regex2 = /"?PROJECT_NAME"?:(.*)/gmi;
                let m2 = regex2.exec(text);
                let prjName = m2 ? m2[1].trim() : '';
                let fParam = [];
                let regex = /postData\[functionParams\]\[([0-9+])\](\[?\]?):(.*)/gmi;
                let m = regex.exec(text);
                if (m && m.length) {
                    parseRegexMatches(regex, m, fParam, text);
                } else {
                    regex = /postData\[functionParams\]:(.*)/gmi;
                    m = regex.exec(text);
                    if (m && m.length === 2) { // NOSONAR
                        fParam = JSON.parse(m[1]);
                    }
                }
                /* eslint-disable no-await-in-loop */
                await this.sendRemoteRequest(fName, fParam, prjName);
            }
        },
        sendRemoteRequest(fName, fParams, prjName) {
            library.showLoadingIndicator('sendResultsHtml');
            let prevPrjName = PROJECT_NAME;
            if (!prjName) prjName = 'coordinator';
            PROJECT_NAME = prjName; // NOSONAR
            let inst = this.currentInstance;
            return this.sDs.post("sds_execRemoteRequest", inst, fName, fParams).then((response) => {
                if (response.status === 'success') {
                    let json = response.data.status === 'success' ? response.data.data : response.data;
                    this.sendResultsClipboard += (this.sendResultsClipboard.length ? '\n' : '') + (typeof html !== 'string' ? JSON.stringify(json, false, 2) : json); // NOSONAR
                    this.setBeautifyResults(json);
                    PROJECT_NAME = prevPrjName;
                    library.hideLoadingIndicator('sendResultsHtml');
                    if (response.data.statisticInfo) this.statisticInfo += response.data.statisticInfo + "\n";
                }
            });
        },
        setBeautifyResults(json) {
            let html = json;
            if (typeof html !== 'string') {
                html = JSON.stringify(html, false, 2); // NOSONAR
                html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                html = html.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, function (match) {
                    let cls = 'number';
                    if (/^"/.test(match)) {
                        if (/:$/.test(match)) {
                            cls = 'key';
                        } else {
                            cls = 'string';
                        }
                    } else if (/true|false/.test(match)) {
                        cls = 'boolean';
                    } else if (/null/.test(match)) {
                        cls = 'null';
                    }
                    return '<span class="json-' + cls + '">' + match + '</span>';
                });
            }
            this.sendResultsHtml = (this.sendResultsHtml ? this.sendResultsHtml + '<br/><hr><br/>' : '') + '<pre>' + html + '</pre>';
        },
        async sendRequest() {
            this.sendResultsText = '';
            this.sendResultsHtml = '';
            this.sendResultsClipboard = '';
            this.storeParam();
            let selectedFunction = this.selectedFunction;
            let fName = selectedFunction.name;
            let tmpfParams = this.fParams;
            let fParams = {};
            let maxIndex = 0;
            tmpfParams.forEach((tmpItem, index) => {
                let isJson = library.isJson(tmpItem.value);
                if (!isJson && library.isJson(tmpItem.value.replace(/'/g, '"'))) {
                    isJson = true;
                    tmpItem.value = tmpItem.value.replace(/'/g, '"');
                }
                maxIndex = parseInt(index, 10) + 1;
                fParams[maxIndex] = isJson ? JSON.parse(tmpItem.value) : tmpItem.value;
            });
            for (let i = maxIndex; i > 0; i--) {
                if (!fParams[i]) delete fParams[i];
                break;
            }
            this.sendRemoteRequest(fName, fParams);
        },
        async getRequestText() {
            this.sendResultsText = '';
            this.sendResultsHtml = '';
            this.sendResultsClipboard = '';
            this.storeParam();
            let selectedFunction = this.selectedFunction;
            let fName = selectedFunction.name;
            let tmpfParams = this.fParams;
            let fParams = {};
            tmpfParams.forEach((tmpItem, index) => {
                let isJson = library.isJson(tmpItem.value);
                if (!isJson && library.isJson(tmpItem.value.replace(/'/g, '"'))) {
                    isJson = true;
                    tmpItem.value = tmpItem.value.replace(/'/g, '"');
                }
                fParams[parseInt(index, 10) + 1] = isJson ? JSON.parse(tmpItem.value) : tmpItem.value;
            });
            let instName = this.currentInstance;
            this.sDs.post("sds_execRemoteRequest", instName, fName, fParams, false, true).then((response) => {
                this.sendResultsClipboard = response.data;
                this.controls['REQUEST_AS_TEXT'].value = response.data;
                this.selectedTabIndex = 1;
            });
        },
        setFunction: function (value) {
            if (value && value.itemData) {
                this.loadingParams = false;
                this.selectedFunction = value.itemData;
                this.fParams = this.selectedFunction.params;
                setTimeout(() => {
                    this.loadingParams = true;
                }, 50); // NOSONAR
            }
        },
        selectTab: function (e) {
            this.selectedTabIndex = e.selectedIndex;
        },
        fixPayload: function (theText) {
            theText = theText.trim();
            theText = theText.replace(/\n/g, ""); // remove all new lines
            for (let i in this.availablePostParams) {
                let preparedPostParam = this.availablePostParams[i].replaceAll("[", "\\[").replaceAll("]", "\\]");
                let regex = new RegExp('"?' + preparedPostParam + '"?[^:[]', "gmi");
                if (regex.test(theText)) {
                    theText = theText.replace(this.availablePostParams[i], this.availablePostParams[i] + ':');
                }
                // add new lines before the post params only
                theText = theText.replace(this.availablePostParams[i], '\n' + this.availablePostParams[i]);
            }
            theText = theText.trim('\n'); // remove new lines from start/end
            return theText;
        },
        payloadInput(e) {
            if (e && e?.event?.inputType === 'insertFromPaste') {
                this.controls['REQUEST_AS_TEXT'].value = this.fixPayload(e.value);
            }
        }
    },
    components: {
        "ejs-tab": defineAsyncComponent(() => import('@common/syncfusion/tab')),
        "e-tabitems": TabItemsDirective,
        "e-tabitem": TabItemDirective
    }
};

componentStore.initComponent(sdsConsolePanelCmp, sdsConsolePanelCfg);
export { sdsConsolePanelCfg, sdsConsolePanelCmp };
export default sdsConsolePanelCmp;
</script>

<style scoped>
    .sds-console-panel-container { height: 100%; }
    .sds-console-panel-function-list {
        position: sticky;
        top: 20px;
    }
    .sds-console-panel-function-list-inner {
        display: flex;
        flex-direction: column;
    }
    .sds-console-panel-function-list-inner2 {
        overflow-y: auto;
    }
    .sds-console-panel-fparam { font-size: 20px !important; margin-left: 5px; width:100%; text-align:left; padding:0px; }
    .sds-console-panel-button { display: inline-block; position: relative; margin-right: 10px; }
    .sds-console-panel-button:last-child { margin-right: 0 }
    .results-column { padding-top: 10px; display: flex; flex-direction: column; height: 100%; }
    .sds-console-panel-results { font-size:12pt; height:98%; overflow-y:auto; border:1px; border-style: dotted; padding:3px; flex-grow: 1; }
    :deep(.sds-console-panel-results pre) { margin: 0 !important; }
    .sds-console-panel-desc { font-size:11pt; white-space: pre-wrap; border:1px; border-style: dashed; padding:2px; }
    .sds-console-panel-statisticInfo { font-size:9pt; white-space: pre-wrap; width:95%; padding:0px; }
    :deep(.send-button) { padding: 10px; }
    :deep(.json-string) { color: green; }
    :deep(.json-number) { color: darkorange; }
    :deep(.json-boolean) { color: blue; }
    :deep(.json-null) { color: magenta; }
    :deep(.json-key) { color: purple; }
    [cid="sdsConsolePanel"] { height: 100%; }
    :deep(.e-tab .e-content > .e-item) {
        margin: 10px 0;
    }
    .send-buttons-container {
        text-align: right;
        padding-top: 10px;
        margin-top: 20px;
        border-top: 1px solid #a4a4a4;
    }
</style>
